import map from "lodash/map"
import isEmpty from "lodash/isEmpty"
import filter from "lodash/filter"
import each from "lodash/each"
import find from "lodash/find"
import get from "lodash/get"
import { useState, useEffect } from "react"
import Box from "component-lib/Base/components/Box"
import { makeStyles } from "tss-react/mui"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import getContentUrl from "utils-lib/getContentUrl"
import getAlertVersion from "utils-lib/alerts"
import { convertUrlQueryParams } from "utils-lib/convertUrlQueryParams"
import { getWindow } from "utils-lib/getWindow"
import { isMyWMApp } from "utils-lib/isMyWMApp"
import { BOOTSTRAP_GRID_IPAD_PRO_MAX } from "constants-lib/dimensions"
import { Button } from "../../../Base"
import AlertsCarousel from "./components/AlertsCarousel/AlertsCarousel"
import GlobalAlert from "./components/GlobalAlert/GlobalAlert"
import BrowserAlert from "./components/BrowserAlert/BrowserAlert"
import PrivacyAlert from "./components/PrivacyAlert/PrivacyAlert"

const useStyles = makeStyles()(
  (
    theme,
    {
      isRedesignedWMHeader,
      isMobileRedesignedWMHeader,
      isAccountsDashboardPage,
    },
  ) => ({
    globalNotifications: {
      position: "absolute",
      width: "100%",
      color: theme.palette.text.secondary,
      left: 0,
      marginTop: !isAccountsDashboardPage
        ? isMobileRedesignedWMHeader && `75px`
        : isMobileRedesignedWMHeader && `55px`,
      top: isRedesignedWMHeader ? `64px` : `119px`,
      zIndex: "100",
      "& .linkText": {
        textDecoration: "underline !important",
      },
      "& .title": {
        verticalAlign: "super",
      },
      "& .subtext": {
        verticalAlign: "top",
      },
      [theme.breakpoints.down(`lg`)]: {
        top: isAccountsDashboardPage ? "70px" : "55px",
        marginTop: !isAccountsDashboardPage ? "0" : "55px",
        backgroundColor: theme.palette.background.beige,
      },
    },
    containerCloseAllButton: {
      display: "flex",
      alignItems: "center",
      height: "57px",
      position: "absolute",
      right: "4%",
    },
    oneAlert: {
      height: "46px",
    },
    majorAlertCloseAllBtn: {
      cursor: "pointer",
      display: "flex",
      justifyContent: "end",
      "& img": {
        zIndex: 10,
      },
    },
    notificationCloseBtn: {
      background: "transparent",
      cursor: "pointer",
      float: "right",
      display: "flex",
      justifyContent: "end",
      backgroundColor: "transparent",
      "&:focus, &:active": {
        outlineColor: "initial",
        outlineStyle: "auto",
        outlineWidth: "5px",
      },
      "& .iconClose": {
        verticalAlign: "sub",
      },
    },
    [theme.breakpoints.down(`md`)]: {
      majorAlertCloseAllBtn: {
        marginTop: "8%",
      },
    },
  }),
)

const GlobalNotifications = ({
  onAlertClose,
  onAlertsChange,
  megaNavSelection = "",
  isRedesignedWMHeader = false,
  isMobileRedesignedWMHeader = false,
  alerts,
  alert,
  state,
  minorAlertNotVisible,
  renderDashboardTabs,
  isAccountsDashboardPage,
  isAccountsDetailsPage,
}) => {
  const ssrWindow = getWindow()

  const {
    LDFFEnableBanneronPages,
    LDFFReleaseFixprivacybanner,
    LDFFReleaseImplementMultiAccountDashboard,
  } = useFeatureFlags([
    "LDFFEnableBanneronPages",
    "LDFFReleaseFixprivacybanner",
    "LDFFReleaseImplementMultiAccountDashboard",
  ])

  const { classes } = useStyles({
    isRedesignedWMHeader,
    isMobileRedesignedWMHeader,
    LDFFReleaseImplementMultiAccountDashboard,
    isAccountsDashboardPage,
  })

  const isMobile = ssrWindow.innerWidth <= BOOTSTRAP_GRID_IPAD_PRO_MAX

  const [closeAll, setCloseAll] = useState(false)
  let globalAlert

  const sendDataToParent = (index) => {
    if (index < 2) {
      setCloseAll(true)
    }
  }
  useEffect(() => {
    setCloseAll(JSON.parse(ssrWindow.localStorage.getItem("closeAll")))
  }, [])

  useEffect(() => {
    ssrWindow.localStorage.setItem("closeAll", closeAll)
  }, [closeAll])

  function isNotificationHidden(subtext) {
    const ALERT_VERSION = getAlertVersion()
    const LOCAL_STORAGE_KEY = `${ALERT_VERSION}-alreadyVisited`

    const visitedAlerts = localStorage.getItem(LOCAL_STORAGE_KEY)

    let parseVisitedAlerts = {}
    if (visitedAlerts) {
      try {
        parseVisitedAlerts = JSON.parse(visitedAlerts)

        const hasAlertBeenSeen = get(parseVisitedAlerts, subtext, false)

        if (hasAlertBeenSeen) {
          return true
        }
      } catch {
        /* do nothing, use default parseVisitedAlerts value */
      }
      return false
    }
    return false
  }

  if (ssrWindow.location.search) {
    const params = convertUrlQueryParams(ssrWindow.location.search)

    if (params.referrer) {
      const { referrer } = params

      globalAlert = find(
        alerts,
        (item) => item.trackingCode === referrer || item.trackingCode === ``,
      )

      if (
        globalAlert &&
        !ssrWindow.localStorage.getItem(
          `${globalAlert.trackingCode}_initialized`,
        )
      ) {
        ssrWindow.localStorage.setItem(
          `${globalAlert.trackingCode}_initialized`,
          true,
        )
      }
    }
  }

  each(alerts, (item) => {
    if (ssrWindow.localStorage.getItem(`${item.trackingCode}_initialized`)) {
      globalAlert = item
    }
  })

  const minorAlerts = filter(alerts, (item) => item.alertType === `minorAlerts`)
  const nonMinorAlerts = filter(
    alerts,
    (item) => item.alertType !== `minorAlerts`,
  )

  const sortedAlerts = nonMinorAlerts.concat(minorAlerts)
  const firstPrivacyAlert = find(
    sortedAlerts,
    (item) => item.alertType === `privacyAlerts`,
  )

  const alertsFinal = filter(sortedAlerts, (item) => {
    let isHidden = isNotificationHidden(item.title)

    if (item.alertType !== `majorAlerts` && item.alertType !== `minorAlerts`) {
      isHidden = true
    }

    if (!isHidden) {
      if (item.alertType === `minorAlerts`) {
        if (
          get(item, `stateList`, ``)
            .toLowerCase()
            .indexOf(state.toLowerCase()) === -1
        ) {
          isHidden = true
        }

        if (!alert.minorAlertIsvisible) {
          isHidden = true
        }
      }
    }
    return !isHidden
  })

  const alertLocationWise = sortedAlerts.filter((item) => {
    const stateName = ssrWindow.location.pathname.split("/")
    const index = stateName.indexOf("location")
    const state = stateName[index + 1]?.toUpperCase()
    const minorAlertStateList = get(item, `stateList`)
    const minorAlertStateListarray = minorAlertStateList?.split(`;`)
    const findStateInStateList = minorAlertStateListarray?.includes(state)
    if (
      ssrWindow.location.pathname.indexOf("/location/") > -1 &&
      item.alertType === `minorAlerts` &&
      findStateInStateList
    ) {
      const locationWiseminorAlerts = filter(
        alerts,
        (item) =>
          item.alertType === `minorAlerts` && item.stateList?.includes(state),
      )
      return locationWiseminorAlerts
    }
    return null
  })

  const concatsortedAlerts = alertsFinal?.concat(alertLocationWise)

  return (
    <Box
      className={classes.globalNotifications}
      data-testid="GlobalNotifications"
    >
      {!closeAll && !isEmpty(concatsortedAlerts) && (
        <>
          <Box
            className={`${classes.containerCloseAllButton} ${classes.oneAlert}`}
            data-testid="close-all-button"
          >
            <Button
              type="button"
              alt="close alert"
              className={`${
                sortedAlerts.alertType === `privacyAlerts`
                  ? classes.notificationCloseBtn
                  : classes.majorAlertCloseAllBtn
              } `}
              onClick={() => {
                setCloseAll(true)
              }}
            >
              <img
                src={getContentUrl(
                  "/content/dam/wm/icons/white-close-x-icon.svg",
                )}
                alt="close-icon"
                style={{ width: 16, height: 16 }}
              />
            </Button>
          </Box>
          <AlertsCarousel
            state={state}
            onAlertsChange={onAlertsChange}
            onAlertClose={onAlertClose}
            minorAlertNotVisible={minorAlertNotVisible}
            alertsFinal={concatsortedAlerts}
            alertState={alert}
            megaNavSelection={megaNavSelection}
            sendDataToParent={sendDataToParent}
          />
        </>
      )}
      {globalAlert && !isMyWMApp && (
        <GlobalAlert
          title={globalAlert.title}
          subtext={globalAlert.subtext}
          trackingCode={globalAlert.trackingCode}
          logo={globalAlert.logo}
          iconClose={globalAlert.iconClose}
        />
      )}
      {map(sortedAlerts, (alertObject) => {
        const alertData = {
          logo: alertObject.logo,
          title: alertObject.title,
          linktext: alertObject.linktext,
          href: alertObject.href,
          subtext: alertObject.subtext,
          iconClose: alertObject.iconClose,
          backgroundColor: alertObject.backgroundColor,
        }

        if (alertObject.alertType === `browserAlerts`) {
          return (
            <BrowserAlert
              key={`title-${alertObject.title}`}
              browserAlerts={alertData}
              onAlertsChange={onAlertsChange}
              onAlertClose={onAlertClose}
            />
          )
        }

        return null
      })}
      {(firstPrivacyAlert || LDFFReleaseFixprivacybanner) &&
        (LDFFEnableBanneronPages || !isMyWMApp) && (
          <PrivacyAlert
            data-testid="PrivacyAlert"
            showOnAllPages={LDFFEnableBanneronPages}
            minorAlerts={
              firstPrivacyAlert
                ? {
                    logo: firstPrivacyAlert.logo,
                    title: firstPrivacyAlert.title,
                    linktext: firstPrivacyAlert.linktext,
                    href: firstPrivacyAlert.href,
                    subtext: firstPrivacyAlert.subtext,
                    iconClose: firstPrivacyAlert.iconClose,
                    backgroundColor: firstPrivacyAlert.backgroundColor,
                  }
                : null
            }
            onAlertClose={onAlertClose}
            onMinorAlertClose={minorAlertNotVisible}
            alertState={alert}
          />
        )}
      {LDFFReleaseImplementMultiAccountDashboard &&
        !isAccountsDetailsPage &&
        isMobileRedesignedWMHeader &&
        isMobile &&
        renderDashboardTabs?.()}
    </Box>
  )
}

// // Note: React 19 no-longer supports propTypes. Leaving this for reference.
// GlobalNotificationsLegacy.propTypes = {
//   onAlertClose: PropTypes.func.isRequired,
//   onAlertsChange: PropTypes.func.isRequired,
//   megaNavSelection: PropTypes.string,
//   isRedesignedWMHeader: PropTypes.bool,
//   isMobileRedesignedWMHeader: PropTypes.bool,
//   /* AEM props. */
//   alerts: PropTypes.array.isRequired,
//   // From Redux State
//   alert: PropTypes.object.isRequired,
//   state: PropTypes.string.isRequired,
//   // From Redux Action
//   minorAlertNotVisible: PropTypes.func.isRequired,
// }

// // Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// GlobalNotificationsLegacy.defaultProps = {
//   megaNavSelection: "",
//   isRedesignedWMHeader: false,
//   isMobileRedesignedWMHeader: false,
// }

export default GlobalNotifications
